import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Paper,
  useTheme,
  alpha,
  Typography,
  Button,
  Link as MuiLink,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  Container
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  AttachMoney as CostIcon,
  Description as DescriptionIcon,
  Factory as FactoryIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  LocalShipping as LocalShippingIcon,
  BookmarkBorder as BookmarkBorderIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Summarize as SummarizeIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import ProductionLineOverview from './ProductionLineOverview';
import ProductionLineCosts from './ProductionLineCosts';
import ProductionLineLogs from './ProductionLineLogs';
import ProductionLineSummary from './ProductionLineSummary';
import ProductionLineItems from './ProductionLineItems';
import { supabase } from '../../../supabaseClient';
import { usePermissions } from '../../../utils/permissions';

// Styled components matching FormulationTabs patterns
const SectionPaper = styled(Paper)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  padding: theme.spacing(4),
  position: 'relative',
  borderRadius: 16,
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.25)}`
  }
}));

// Custom Timeline Connector
const TimelineConnector = styled(StepConnector)(({ theme, active, completed }) => ({
  '& .MuiStepConnector-line': {
    height: 4,
    border: 0,
    backgroundColor: completed ? '#4caf50' : active ? '#ff9800' : '#e0e0e0',
    borderRadius: 2,
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    '&::after': completed ? {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
      animation: 'shimmer 2s infinite'
    } : {}
  },
  '@keyframes shimmer': {
    '0%': { left: '-100%' },
    '100%': { left: '100%' }
  }
}));

// Custom Timeline Step Icon
const TimelineStepIcon = styled('div')(({ theme, active, completed, stepIndex }) => {
  const colors = [theme.palette.primary.main, theme.palette.success.main, theme.palette.warning.main, theme.palette.info.main, '#9c27b0'];
  const bgColor = completed ? theme.palette.success.main : active ? colors[stepIndex] : '#e0e0e0';
  
  return {
    width: 56,
    height: 56,
    borderRadius: '50%',
    backgroundColor: bgColor,
    border: active ? `3px solid ${alpha(colors[stepIndex] || '#1976d2', 0.3)}` : completed ? `3px solid ${theme.palette.success.main}` : '3px solid #f5f5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: completed || active ? 'white' : '#999',
    transition: 'all 0.3s ease',
    position: 'relative',
    cursor: 'pointer',
    boxShadow: active ? `0 0 20px ${alpha(colors[stepIndex] || '#1976d2', 0.4)}` : completed ? `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}` : '0 2px 8px rgba(0,0,0,0.1)',
    '&:hover': {
      transform: 'scale(1.1)',
      boxShadow: `0 6px 20px ${alpha(bgColor || '#e0e0e0', 0.4)}`
    },
    '& .MuiSvgIcon-root': {
      fontSize: 28
    }
  };
});

// Timeline Component
function LineProductionTimeline({ activeStep, onStepClick, lineProduction, costAnalysisCompleted, productionCompleted, itemsCompleted, isCreatingNew, availableTabs, permissions }) {
  const theme = useTheme();
  
  const allTimelineSteps = [
    {
      label: 'Planning',
      sublabel: 'Line Production Overview',
      icon: <AssignmentIcon />,
      description: 'Define line production parameters and specifications',
      completed: !!(lineProduction?.id)
    },
    {
      label: 'Items',
      sublabel: 'Product Items Setup',
      icon: <InventoryIcon />,
      description: 'Configure product items and quantities',
      completed: itemsCompleted
    },
    {
      label: 'Analysis',
      sublabel: 'Cost Calculation',
      icon: <CostIcon />,
      description: 'Review costs and profitability',
      completed: costAnalysisCompleted
    },
    {
      label: 'Production Log',
      sublabel: 'Line Process',
      icon: <BookmarkBorderIcon />,
      description: 'Execute production and quality control',
      completed: productionCompleted
    },
    {
      label: 'Summary',
      sublabel: 'Final Review',
      icon: <SummarizeIcon />,
      description: 'Complete line production summary and documentation',
      completed: !!(lineProduction?.status === 'approved' || lineProduction?.status === 'completed')
    }
  ];

  // Filter timeline steps based on permissions
  const timelineSteps = allTimelineSteps.filter((step, index) => {
    switch (index) {
      case 0: // Overview
        return permissions.canViewLineOverview();
      case 1: // Items
        return permissions.canViewLineItems();
      case 2: // Analysis (Cost)
        return permissions.canViewLineCost();
      case 3: // Production Log
        return permissions.hasProductionLogAccess;
      case 4: // Summary
        return permissions.canViewLineSummary();
      default:
        return false;
    }
  });

  return (
    <Box sx={{ 
      p: 4,
      borderRadius: 4,
      mb: 4,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
      }
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main', mb: 1 }}>
            Production Timeline
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            Track your progress through the line production process
          </Typography>
        </Box>
        <Box sx={{ 
          px: 3, 
          py: 1, 
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          borderRadius: 2
        }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
            Step {activeStep + 1} of {timelineSteps.length}
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ 
        p: 4, 
        borderRadius: 3, 
        mb: 4, 
        background: 'white',
        border: `1px solid ${alpha(theme.palette.grey[400], 0.9)}`
      }}>
        <Stepper 
          activeStep={activeStep} 
          connector={<TimelineConnector />}
          sx={{ 
            '& .MuiStepLabel-root': {
              cursor: 'pointer'
            }
          }}
        >
          {timelineSteps.map((step, index) => {
            const isActive = index === activeStep;
            const isCompleted = step.completed;
            
            return (
              <Step 
                key={step.label} 
                onClick={() => onStepClick(index)}
                sx={{
                  cursor: 'pointer'
                }}
              >
                <StepLabel
                  StepIconComponent={() => (
                    <TimelineStepIcon 
                      active={isActive} 
                      completed={isCompleted}
                      stepIndex={index}
                    >
                      {isCompleted ? <CheckCircleIcon /> : step.icon}
                    </TimelineStepIcon>
                  )}
                  sx={{
                    '& .MuiStepLabel-labelContainer': {
                      mt: 2
                    }
                  }}
                >
                  <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 500, 
                        color: isCompleted ? '#4caf50' : isActive ? 'primary.main' : 'text.secondary',
                        mb: 0.5,
                        transition: 'color 0.3s ease'
                      }}
                    >
                      {step.label}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: isCompleted ? '#4caf50' : isActive ? 'primary.main' : 'text.secondary',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        mb: 1
                      }}
                    >
                      {step.sublabel}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'text.secondary',
                        display: 'block',
                        maxWidth: 140,
                        lineHeight: 1.3
                      }}
                    >
                      {step.description}
                    </Typography>
                    
                    {/* Status Indicator */}
                    <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: isCompleted ? theme.palette.success.main : isActive ? theme.palette.warning.main : 'text.secondary',
                          fontWeight: 500,
                          fontSize: '0.7rem',
                          textTransform: 'uppercase',
                          px: 1.5,
                          py: 0.5,
                          backgroundColor: alpha(isCompleted ? theme.palette.success.main : isActive ? theme.palette.warning.main : theme.palette.grey[500], 0.1),
                          borderRadius: 1,
                          border: `1px solid ${alpha(isCompleted ? theme.palette.success.main : isActive ? theme.palette.warning.main : theme.palette.grey[500], 0.2)}`,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {isCompleted ? (
                          '✓ Complete'
                        ) : (
                          <>
                            {isActive && <ScheduleIcon sx={{ fontSize: 12 }} />}
                            {isActive ? 'In Progress' : 'Pending'}
                          </>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Paper>

      {/* Progress Bar */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
            Overall Progress
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
            {Math.round((timelineSteps.filter(s => s.completed).length / timelineSteps.length) * 100)}%
          </Typography>
        </Box>
        <Box sx={{ 
          width: '100%', 
          height: 8, 
          backgroundColor: alpha(theme.palette.grey[400], 0.3),
          borderRadius: 4,
          overflow: 'hidden',
          position: 'relative'
        }}>
          <Box sx={{
            width: `${(timelineSteps.filter(s => s.completed).length / timelineSteps.length) * 100}%`,
            height: '100%',
            background: `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.success.light})`,
            transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
            borderRadius: 4,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: 20,
              height: '100%',
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 100%)',
              animation: 'progressShine 2s infinite'
            }
          }} />
        </Box>
      </Box>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="outlined"
          onClick={() => {
            const prevStep = Math.max(0, activeStep - 1);
            onStepClick(prevStep);
          }}
          disabled={activeStep === 0}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            borderColor: 'primary.main',
            color: 'primary.main',
            fontWeight: 500,
            background: 'white',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
            '&:hover': {
              borderColor: 'primary.dark',
              backgroundColor: 'white',
              transform: 'translateY(-1px)',
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
            },
            '&:disabled': {
              borderColor: alpha(theme.palette.grey[400], 0.5),
              color: 'text.disabled',
              backgroundColor: 'white'
            },
            transition: 'all 0.2s ease'
          }}
        >
          ← Previous Step
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {timelineSteps.map((_, index) => (
            <Box
              key={index}
              onClick={() => onStepClick(index)}
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: index === activeStep ? 'primary.main' : index < activeStep ? 'success.main' : alpha(theme.palette.grey[400], 0.5),
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.3)',
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                }
              }}
            />
          ))}
        </Box>

        <Button
          variant="contained"
          onClick={() => {
            const nextStep = Math.min(timelineSteps.length - 1, activeStep + 1);
            onStepClick(nextStep);
          }}
          disabled={activeStep === timelineSteps.length - 1}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            color: 'white',
            fontWeight: 500,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
              transform: 'translateY(-1px)',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`
            },
            '&:disabled': {
              background: alpha(theme.palette.grey[400], 0.5),
              color: 'text.disabled'
            },
            transition: 'all 0.2s ease'
          }}
        >
          Next Step →
        </Button>
      </Box>
    </Box>
  );
}

const ProductionLineTabs = forwardRef(({ 
  lineProduction, 
  products,
  rawMaterials,
  showSnackbar,
  user,
  userCompanyId,
  onUpdate,
  onBackToList,
  lineProductionTimeFormat = '12hour'
}, ref) => {
  const { companyId } = useParams();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [costAnalysisCompleted, setCostAnalysisCompleted] = useState(false);
  const [productionCompleted, setProductionCompleted] = useState(false);
  const [itemsCompleted, setItemsCompleted] = useState(false);

  // Get permissions
  const { 
    canCreateLine,
    canViewLineOverview,
    canEditLineOverview,
    canViewLineItems,
    canViewLineCost,
    canViewLineSummary,
    canViewLineLogOverview,
    canViewLineLogChecklist,
    canViewLineLogSupply,
    canViewLineLogYield,
    canViewLineLogSignatures
  } = usePermissions();

  // Check if user has access to any production log components
  const hasProductionLogAccess = canViewLineLogOverview() || canViewLineLogChecklist() || canViewLineLogSupply() || canViewLineLogYield() || canViewLineLogSignatures();

  // Check if user has limited permissions (can create but missing required permissions for line overview)
  const hasLimitedPermissions = canCreateLine() && (!canViewLineOverview() || !canEditLineOverview());
  const isCreatingNew = !lineProduction?.id;

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    openEditDialog: (isCreatingNew = false) => {
      if (activeTab === 0) {
        // If we're on overview tab, call the edit dialog directly
        // This will be handled by the ProductionLineOverview component
      } else {
        // Switch to overview tab first, then open edit dialog
        setActiveTab(0);
        setTimeout(() => {
          // The ProductionLineOverview should handle this
        }, 100);
      }
    }
  }));

  const handleTimelineStepClick = (stepIndex) => {
    // The timeline steps are already filtered, so stepIndex directly corresponds to tabsData index
    if (stepIndex >= 0 && stepIndex < tabsData.length) {
      setActiveTab(stepIndex);
      
      // Check if this is the production log tab by looking at the component type
      const selectedTab = tabsData[stepIndex];
      if (selectedTab && selectedTab.component && selectedTab.component.type && 
          selectedTab.component.type.name === 'ProductionLineLogs' && lineProduction?.id) {
        checkProductionLogCompletionFromDB();
      }
    }
  };

  const handleCostAnalysisSave = () => {
    setCostAnalysisCompleted(true);
  };

  const handleProductionCompletion = async (isCompleted, completionStatus) => {
    setProductionCompleted(isCompleted);
  };

  const checkItemsCompletion = async () => {
    if (!lineProduction?.id) {
      setItemsCompleted(false);
      return;
    }

    try {
      const { data: items, error } = await supabase
        .from('production_line_items')
        .select('id, actual_quantity, item_condition')
        .eq('line_production_id', lineProduction.id);

      if (error) throw error;

      // Check if all items have both actual quantities (lot/batch selections) and quality conditions
      const allItemsComplete = items && items.length > 0 && items.every(item => 
        item.actual_quantity && item.actual_quantity > 0 && item.item_condition
      );

      setItemsCompleted(allItemsComplete);
    } catch (error) {
      console.error('Error checking items completion:', error);
      setItemsCompleted(false);
    }
  };

  // Check production log completion from database
  const checkProductionLogCompletionFromDB = async () => {
    if (!lineProduction?.id) {
      setProductionCompleted(false);
      return;
    }

    try {
      // Check if production line log exists and check completion status
      const { data: logData, error: logError } = await supabase
        .from('production_line_logs')
        .select('overview_completed, area_checklist_completed, supply_log_completed, summary_log_completed, signature_log_completed')
        .eq('line_production_id', lineProduction.id)
        .single();

      if (logError && logError.code !== 'PGRST116') {
        // 406 = table doesn't exist yet or RLS blocking — silently ignore, not an app error
        setProductionCompleted(false);
        return;
      }

      if (logData) {
        // Check if all 5 steps are completed
        const allStepsComplete = !!(
          logData.overview_completed && 
          logData.area_checklist_completed && 
          logData.supply_log_completed && 
          logData.summary_log_completed && 
          logData.signature_log_completed
        );
        setProductionCompleted(allStepsComplete);
      } else {
        setProductionCompleted(false);
      }

    } catch (error) {
      console.error('Error checking production log completion from DB:', error);
      setProductionCompleted(false);
    }
  };

  const checkCostAnalysisCompletion = async () => {
    if (!lineProduction?.id) {
      setCostAnalysisCompleted(false);
      return;
    }

    try {
      const { data: costs, error } = await supabase
        .from('production_line_costs')
        .select('is_completed')
        .eq('line_production_id', lineProduction.id)
        .maybeSingle();

      if (error) throw error;

      setCostAnalysisCompleted(costs?.is_completed === true);
    } catch (error) {
      console.error('Error checking cost analysis completion:', error);
      setCostAnalysisCompleted(false);
    }
  };

  // Check items, cost, and production log completion when line production changes
  useEffect(() => {
    if (lineProduction?.id) {
      checkItemsCompletion();
      checkCostAnalysisCompletion();
      checkProductionLogCompletionFromDB();
      
      // Set up periodic checking every 5 seconds when on relevant tabs
      const interval = setInterval(() => {
        if (activeTab === 1) { // Items tab
          checkItemsCompletion();
        }
        if (activeTab === 2) { // Cost analysis tab
          checkCostAnalysisCompletion();
        }
        if (activeTab === 3) { // Production log tab
          checkProductionLogCompletionFromDB();
        }
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [lineProduction?.id, activeTab]);

  const allTabsData = [
    {
      label: 'Overview',
      icon: <AssignmentIcon />,
      component: (
        <ProductionLineOverview 
          lineProduction={lineProduction}
          products={products}
          showSnackbar={showSnackbar}
          isCreatingNew={!lineProduction?.id}
          userCompanyId={userCompanyId}
          onUpdate={(updatedLineProduction) => {
            onUpdate(updatedLineProduction);
          }}
          onBackToList={onBackToList}
          user={user}
        />
      )
    },
    {
      label: 'Items',
      icon: <InventoryIcon />,
      component: (
        <ProductionLineItems 
          lineProduction={lineProduction}
          products={products}
          showSnackbar={showSnackbar}
          userCompanyId={userCompanyId}
          onUpdate={(updatedData) => {
            onUpdate(updatedData);
            checkItemsCompletion(); // Check completion when items are updated
          }}
          user={user}
        />
      )
    },
    {
      label: 'Cost Analysis',
      icon: <CostIcon />,
      component: (
        <ProductionLineCosts 
          lineProduction={lineProduction}
          showSnackbar={showSnackbar}
          userCompanyId={userCompanyId}
          onUpdate={(updatedData) => {
            onUpdate(updatedData);
            checkCostAnalysisCompletion(); // Check completion when costs are updated
          }}
          user={user}
          onSave={handleCostAnalysisSave}
        />
      )
    },
    {
      label: 'Production Log',
      icon: <BookmarkBorderIcon />,
      component: (
        <ProductionLineLogs 
          lineProductionId={lineProduction?.id}
          lineProductionData={lineProduction}
          onProductionCompletion={handleProductionCompletion}
          user={user}
        />
      )
    },
    {
      label: 'Summary',
      icon: <SummarizeIcon />,
      component: (
        <ProductionLineSummary 
          lineProduction={lineProduction}
          onPrint={() => window.print()}
          onExport={() => {
            showSnackbar('Export functionality coming soon', 'info');
          }}
          onUpdate={onUpdate}
          showSnackbar={showSnackbar}
          user={user}
          userCompanyId={userCompanyId}
        />
      )
    }
  ];

  // Filter tabs based on permissions
  const tabsData = allTabsData.filter((tab, index) => {
    switch (index) {
      case 0: // Overview
        return canViewLineOverview();
      case 1: // Items
        return canViewLineItems();
      case 2: // Cost Analysis
        return canViewLineCost();
      case 3: // Production Log
        return hasProductionLogAccess;
      case 4: // Summary
        return canViewLineSummary();
      default:
        return false;
    }
  });

  // Auto-set active tab to first available tab if current tab is not accessible
  useEffect(() => {
    if (tabsData.length > 0 && activeTab >= tabsData.length) {
      setActiveTab(0); // Set to first available tab
    }
  }, [tabsData.length, activeTab]);

  // Show access denied message if no tabs are available
  if (tabsData.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        width: '100vw',
        position: 'absolute',
        top: 0,
        left: 0,
        backgroundColor: '#f5f5f5'
      }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          textAlign: 'center',
          width: '100%',
          maxWidth: 600,
          px: 3
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            mx: 'auto',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <FactoryIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Additional Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 4,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You have permission to <strong>create new line productions</strong> only. 
              You need permissions for the following to create a line:
            </Typography>
            <Box sx={{
              textAlign: 'left',
              maxWidth: 400,
              mx: 'auto',
              mb: 6,
              p: 3,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.grey[100], 0.5),
              border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`
            }}>
              <Typography variant="body2" sx={{ 
                fontWeight: 600,
                mb: 1,
                fontSize: '0.9rem'
              }}>
                Line Production Page:
              </Typography>
              <Typography variant="body2" sx={{ 
                ml: 2,
                mb: 0.5,
                fontSize: '0.85rem',
                fontWeight: 500
              }}>
                • View
              </Typography>
              <Typography variant="body2" sx={{ 
                ml: 2,
                mb: 2,
                fontSize: '0.85rem',
                fontWeight: 500
              }}>
                • Create Line
              </Typography>
              <Typography variant="body2" sx={{ 
                fontWeight: 600,
                mb: 1,
                fontSize: '0.9rem'
              }}>
                Line Overview:
              </Typography>
              <Typography variant="body2" sx={{ 
                ml: 2,
                mb: 0.5,
                fontSize: '0.85rem',
                fontWeight: 500
              }}>
                • View
              </Typography>
              <Typography variant="body2" sx={{ 
                ml: 2,
                fontSize: '0.85rem',
                fontWeight: 500
              }}>
                • Edit
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={onBackToList}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Box>
    );
  }



  return (
    <Box>
      {/* Enhanced Page Header */}
      <Card sx={{ 
        borderRadius: 3,
        mb: 4,
        mt: 8,
        border: `1px solid ${theme.palette.grey[400]}`,
        background: theme.palette.grey[100],
        backdropFilter: 'blur(20px)',
        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
        },
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${theme.palette.primary.main}, white)`,
          opacity: 0.8
        }
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box sx={{ 
                p: 2.5, 
                borderRadius: 3, 
                background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                backdropFilter: 'blur(10px)',
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
              }}>
                <FactoryIcon sx={{ fontSize: 36, color: 'text.secondary' }} />
              </Box>
              <Box>
                <Typography variant="h3" sx={{ 
                  fontWeight: 500, 
                  color: 'primary.main', 
                  mb: 1,
                  fontSize: '2.5rem',
                  letterSpacing: '-0.5px'
                }}>
                  {lineProduction?.batch_number || 'New Line Production'}
                </Typography>
                <Typography variant="h6" sx={{ 
                  color: 'text.secondary', 
                  fontWeight: 500,
                  fontSize: '1.1rem'
                }}>
                  Line Production
                </Typography>
              </Box>
            </Box>
            <Button 
              startIcon={<ArrowBackIcon />} 
              onClick={onBackToList}
              variant="outlined"
              sx={{
                borderRadius: 2,
                borderColor: 'grey.400',
                color: 'primary.main',
                borderWidth: 1,
                fontWeight: 500,
                px: 3,
                py: 1.5,
                background: 'white',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                '&:hover': {
                  borderColor: 'grey.600',
                  backgroundColor: 'white',
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.grey[400], 0.25)}`
                }
              }}
            >
              Back to Line Productions
            </Button>
          </Box>
          <Typography variant="body1" sx={{ 
            color: 'text.secondary', 
            maxWidth: 700, 
            lineHeight: 1.6,
            fontSize: '1.05rem',
            fontWeight: 500
          }}>
            Plan, track, and manage line productions from planning to completion with comprehensive monitoring.
          </Typography>
        </CardContent>
      </Card>

      {/* Enhanced Timeline Component */}
      <LineProductionTimeline 
        activeStep={activeTab} 
        onStepClick={handleTimelineStepClick}
        lineProduction={lineProduction}
        costAnalysisCompleted={costAnalysisCompleted}
        productionCompleted={productionCompleted}
        itemsCompleted={itemsCompleted}
        isCreatingNew={!lineProduction?.id}
        availableTabs={tabsData}
        permissions={{
          canViewLineOverview,
          canViewLineItems,
          canViewLineCost,
          canViewLineSummary,
          hasProductionLogAccess
        }}
      />

      {/* Enhanced Main Content Area */}
      <SectionPaper>
        {tabsData[activeTab]?.component}
      </SectionPaper>
      
      <Box sx={{ pb: 16 }} />

      <style>
        {`
          @keyframes progressShine {
            0% { opacity: 0; }
            50% { opacity: 1; }
            100% { opacity: 0; }
          }
        `}
      </style>
    </Box>
  );
});

ProductionLineTabs.displayName = 'ProductionLineTabs';

export default ProductionLineTabs;